import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { salesCategoriesGetAll, salesCategoriesUpdate } from "~/api-refs";
import {
  EMPTY_SALES_CATEGORY_FORM_VALUES,
  SalesCategoryFormModal,
  type SalesCategoryFormValues,
} from "~/components/settings/sales-category-form-modal";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/settings/sales-categories/$categoryId/edit"
)({
  component: EditSalesCategoryModal,
});

function EditSalesCategoryModal() {
  const { categoryId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch the sales category data
  const { data: salesCategories } = useQuery(
    convexQuery(salesCategoriesGetAll, {})
  );

  const salesCategory = salesCategories?.find((cat) => cat._id === categoryId);

  const form = useForm<SalesCategoryFormValues>({
    defaultValues: EMPTY_SALES_CATEGORY_FORM_VALUES,
  });

  // Populate form when data loads
  useEffect(() => {
    if (salesCategory) {
      form.reset({
        name: salesCategory.name,
      });
    }
  }, [salesCategory, form]);

  const updateSalesCategoryMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: Id<"salesCategories">;
      data: { name: string };
    }) => {
      return convexClient.mutation(salesCategoriesUpdate, { id, data });
    },
    onSuccess: () => {
      ProductEvents.salesCategoryUpdated(categoryId);
    },
  });

  const handleClose = () => {
    void navigate({ to: "/settings/sales-categories" });
  };

  const handleSubmit = async (values: SalesCategoryFormValues) => {
    if (updateSalesCategoryMutation.isPending) {
      return;
    }

    try {
      await updateSalesCategoryMutation.mutateAsync({
        id: toConvexTableId("salesCategories", categoryId),
        data: values,
      });
      toast.success("Sales Category updated successfully");
      handleClose();
    } catch (error) {
      toast.error("Error updating Sales Category");
      logger.exception("Failed to update sales category", error);
    }
  };

  if (!salesCategory) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-6" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <SalesCategoryFormModal
      description="Update the sales category name."
      form={form}
      isSubmitting={updateSalesCategoryMutation.isPending}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitLabel="Save"
      title="Edit Sales Category"
    />
  );
}
